import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import mergeClassNames from '../../../lib/style-helpers/merge-class-name';
import ProgressBar from '../../progress-bar/progress-bar';
import fileDisplayInfo from '../../../models/fileDisplayInfo';
import Icon from '../../icons/icon';
import { FileToken } from '../../../types/FileToken';
import { UIProps } from '../../../types/StyledProps';
import localizeDate from '../../../lib/localization/localize-date';
import { FontSize, FontType } from '../../../../../config/theme/types/themeTypes';

export type FileCardProps = React.PropsWithoutRef<UIProps & FileToken>;

function FileCard({
  className,
  delay = 0,
  name,
  type,
  dateUploaded,
  progress = 0,
  ...props
}: FileCardProps) {
  const delayProp = {
    delay: (delay) / 3,
  };

  const { icon, label } = fileDisplayInfo(type);

  return (
    <Container
      className={mergeClassNames('FileCard', className)}
      {...props}
    >
      <Card {...delayProp}>
        <IconWrapper>
          <ImageIcon
            type={icon}
            size={64}
          />
        </IconWrapper>
        <Content>
          <TypeLabel>
            {label}
          </TypeLabel>
          <Title>
            {name}
          </Title>
          <DateStamp>
            {localizeDate(new Date(dateUploaded))}
          </DateStamp>
          <ProgressBar progress={progress} />
        </Content>
      </Card>
    </Container>
  );
}

const Container = styled.div`
  padding: 16px;
  width: 33.33%;

  ${({ theme }) => theme.media.desktop} {
    width: 33.33%;
  }

  ${({ theme }) => theme.media.tablet} {
    width: 100%;
  }

  ${({ theme }) => theme.media.custom(800)} {
    width: 50%;
  }

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
  }
`;

const Card = styled.div <UIProps>`
  z-index: 1;
  display: flex;
  flex-direction: row;
  opacity: 0;
  overflow: hidden;
  position: relative;
  padding: 14px 14px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 10px rgb(0 0 0 / 30%);
  animation: pop-in 0.5s ${({ delay }) => delay}s forwards;
  @keyframes pop-in {
    0% {
      opacity: 0;
      transform: scale(0.5);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Content = styled.div`
  z-index: 1;
  display: flex;
  flex: 1 1 80%;
  flex-direction: column;
  text-align: left;
  padding-left: 12px;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const TypeLabel = styled.div`
  margin-top: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ theme }) => theme.font(FontType.SemiBold, FontSize.Medium)};
  color: ${({ theme }) => theme.colors.text};
`;

const Title = styled.div`
  margin-top: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: bold;
  ${({ theme }) => `
    color: ${theme.colors.text};
    ${theme.font(FontType.Strong, FontSize.Large)};
    ${theme.media.tablet}
    {
      ${theme.font(FontType.Strong, FontSize.Medium)};
    }
  `}
`;

const DateStamp = styled.div`
  padding-top: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ theme }) => theme.font(FontType.Regular, FontSize.Medium)};
  color: ${({ theme }) => theme.colors.text};
`;

const ImageIcon = styled(Icon)` 
${({ theme }) => theme.media.tablet}
  {
    height: 45;
    width: 45;
  }
`;

export default observer(FileCard);
